// ── capstone-collection: useCollection client adapter for submissions list ───

import { useMemo } from "react";
import {
  Badge,
  useCollection,
  type CollectionColumnDef,
  type CollectionFilterDef,
  type UseCollectionResult
} from "@vjal/ui";

import type { CoachingCapstoneSubmissionDto } from "../../services/coaching-readiness-dtos";
import type { CoachingCohortDto } from "../../services/coaching-dtos";
import { readDirectoryInitialState } from "../directory-collection-state";
import { capstoneStatusLabel, capstoneStatusTone } from "./capstone-helpers";

export const CAPSTONE_PAGE_SIZE = 25;
export const CAPSTONE_DEFAULT_SORT = { id: "createdAt", dir: "desc" as const };

export type CapstoneSubmissionRow = CoachingCapstoneSubmissionDto & Record<string, unknown>;

export interface CohortFilterOption {
  id: string;
  label: string;
}

export function cohortOptionsFromCapstoneRows(rows: CoachingCapstoneSubmissionDto[]): CohortFilterOption[] {
  const seen = new Map<string, string>();
  for (const row of rows) {
    const id = (row.cohortId ?? "").trim();
    if (!id) continue;
    if (!seen.has(id)) seen.set(id, id);
  }
  return Array.from(seen, ([id, label]) => ({ id, label })).sort((a, b) => a.label.localeCompare(b.label));
}

/** Pick the cohort filter before the first submissions read (avoids enabled:false + empty posture flash). */
export function deriveInitialCapstoneCohortId(
  cohortOptions: CohortFilterOption[],
  routeSeed?: { filters?: Record<string, unknown> }
): string {
  const fromRoute = routeSeed?.filters?.cohort_id;
  if (typeof fromRoute === "string" && fromRoute.trim()) return fromRoute;
  return cohortOptions[0]?.id ?? "";
}

export function cohortOptionsFromCohorts(cohorts: CoachingCohortDto[]): CohortFilterOption[] {
  return cohorts
    .map((cohort) => ({ id: cohort.id, label: cohort.name || `Cohort ${cohort.id}` }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

export function buildCapstoneFilterDefs(
  cohortOptions: CohortFilterOption[],
  rows: CoachingCapstoneSubmissionDto[]
): CollectionFilterDef<CapstoneSubmissionRow>[] {
  const statusOptions = Array.from(
    new Set(rows.map((row) => (row.status ?? "").trim()).filter(Boolean))
  ).sort();

  return [
    {
      id: "cohort_id",
      label: "Cohort",
      type: "select",
      placeholder: "All cohorts",
      getValue: (row) => row.cohortId ?? "",
      options: cohortOptions.map((option) => ({ value: option.id, label: option.label }))
    },
    {
      id: "status",
      label: "Status",
      type: "select",
      placeholder: "All statuses",
      getValue: (row) => row.status ?? "",
      options: statusOptions.map((status) => ({ value: status, label: capstoneStatusLabel(status) }))
    }
  ];
}

export function buildCapstoneCollectionColumns(): CollectionColumnDef<CapstoneSubmissionRow>[] {
  return [
    {
      id: "id",
      header: "Submission",
      sortable: true,
      sortAccessor: (row) => row.id,
      searchValue: (row) => `${row.id} ${row.submitterEmail ?? ""}`,
      priority: 1,
      width: "7rem",
      cell: (row) => <span className="inline-block min-w-[5rem] whitespace-nowrap font-medium text-strong">#{row.id}</span>
    },
    {
      id: "submitterEmail",
      // Gemini residual: Submitter header/cells hard-clip mid-email without scroll.
      // Reserve width + truncate with full title tooltip.
      header: <span className="whitespace-nowrap">Submitted by</span>,
      sortable: true,
      sortAccessor: (row) => row.submitterEmail ?? "",
      priority: 1,
      width: "16rem",
      cell: (row) => (
        <span
          className="block min-w-[14rem] max-w-[22rem] truncate whitespace-nowrap text-sm"
          title={row.submitterEmail ?? undefined}
        >
          {row.submitterEmail ?? "—"}
        </span>
      )
    },
    {
      id: "status",
      header: "Status",
      sortable: true,
      sortAccessor: (row) => row.status ?? "",
      priority: 2,
      cell: (row) => (
        <Badge variant={capstoneStatusTone(row.status) === "danger" ? "danger" : capstoneStatusTone(row.status)}>
          {capstoneStatusLabel(row.status)}
        </Badge>
      )
    },
    {
      id: "reportId",
      header: "Report",
      sortable: false,
      priority: 1,
      width: "8rem",
      cell: (row) =>
        row.reportId ? (
          <span className="inline-flex min-w-[7rem] whitespace-nowrap">
            <Badge variant="info">Report linked</Badge>
          </span>
        ) : (
          <span className="inline-flex min-w-[7rem] whitespace-nowrap">
            <Badge variant="default">No report yet</Badge>
          </span>
        )
    },
    {
      id: "createdAt",
      header: "Created",
      sortable: true,
      sortAccessor: (row) => row.createdAt ?? "",
      priority: 2,
      cell: (row) => <span className="text-sm text-ink-muted">{row.createdAt ?? "—"}</span>
    }
  ];
}

export function useCapstoneCollection(
  rows: CoachingCapstoneSubmissionDto[],
  cohortOptions: CohortFilterOption[],
  routePath?: string
): UseCollectionResult<CapstoneSubmissionRow> {
  const filters = useMemo(() => buildCapstoneFilterDefs(cohortOptions, rows), [cohortOptions, rows]);
  const columns = useMemo(() => buildCapstoneCollectionColumns(), []);
  const initialState = useMemo(() => {
    const fromRoute = readDirectoryInitialState(routePath, CAPSTONE_PAGE_SIZE);
    const defaultCohort = cohortOptions[0]?.id;
    const filters = { ...fromRoute.filters };
    if (defaultCohort && filters.cohort_id === undefined) filters.cohort_id = defaultCohort;
    return { sort: CAPSTONE_DEFAULT_SORT, ...fromRoute, filters };
  }, [cohortOptions, routePath]);

  return useCollection<CapstoneSubmissionRow>({
    mode: "client",
    rows: rows as CapstoneSubmissionRow[],
    columns,
    filters,
    pageSize: CAPSTONE_PAGE_SIZE,
    urlSync: "query",
    views: "coaching-capstone-submissions",
    initialState,
    getRowKey: (row) => row.id
  });
}
